import {
  ApiOutlined,
  AppstoreOutlined,
  BarChartOutlined,
  BellOutlined,
  CalendarOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  EditOutlined,
  EyeOutlined,
  FileTextOutlined,
  FilterOutlined,
  GlobalOutlined,
  LockOutlined,
  MailOutlined,
  MobileOutlined,
  RocketOutlined,
  SearchOutlined,
  SettingOutlined,
  SyncOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  UserOutlined,
  VideoCameraOutlined
} from "@ant-design/icons";
import React from "react";

export interface SubFeature {
  title: string;
  items: string[];
}

export interface Feature {
  id: number;
  title: string;
  portal: string;
  icon: React.ReactNode;
  description: string;
  benefits?: string[];
  subFeatures?: SubFeature[];
  howToAccess?: string;
  note?: string;
}

export interface ReleaseNoteContent {
  version: string;
  releaseDate: string;
  previousVersion: string;
  overview: string;
  features: Feature[];
  bugFixes: string[];
  terminologyChanges?: { old: string; new: string }[];
  systemRequirements?: {
    browsers: string[];
    aiAssessments: string[];
  };
  knownIssues?: string[];
  deprecations?: string[];
}

// Version 2.0.3 Content
const v203Content: ReleaseNoteContent = {
  version: "2.0.3",
  releaseDate: "January 13, 2026",
  previousVersion: "2.0.2 (December 23, 2025)",
  overview:
    "QualifyMe v2.0.3 brings significant enhancements focused on integration capabilities, international support, and improved assessment monitoring. This release introduces API & SSO integration options, multi-currency pricing, bulk email capabilities, and enhanced proctoring features for a more comprehensive hiring experience.",
  features: [
    {
      id: 1,
      title: "Resources Center",
      portal: "Admin Portal",
      icon: <FileTextOutlined />,
      description:
        "A new centralized Resources section is now available in the sidebar, replacing the previous Help section.",
      benefits: [
        "Access Frequently Asked Questions (FAQ) organized by category with search and filter options",
        "View Release Notes for all platform versions directly within the application",
        "Contact support directly from the Resources page",
      ],
      howToAccess: 'Click on "Resources" in the left sidebar navigation.',
    },
    {
      id: 2,
      title: "API & Integration Hub",
      portal: "Admin Portal",
      icon: <ApiOutlined />,
      description:
        "Enterprise and Pro plan users now have access to powerful integration capabilities.",
      subFeatures: [
        {
          title: "API Integration",
          items: [
            "Create API Clients: Generate OAuth client credentials for integrating QualifyMe with your existing systems",
            "Manage Multiple Clients: View, edit, activate, or deactivate API clients from a centralized dashboard",
            "API Documentation: Access comprehensive API docs directly from the integration settings",
          ],
        },
        {
          title: "Single Sign-On (SSO)",
          items: [
            "Configure SSO: Set up Single Sign-On with your organization's identity provider",
            "OAuth Configuration: Configure OAuth Provider URL, Token URL, Client credentials, Scopes, and User Info URL",
            "Easy Management: Edit or update SSO settings at any time",
          ],
        },
      ],
      howToAccess: "Go to Organization Settings → Integration → API or SSO tab",
    },
    {
      id: 3,
      title: "Multi-Currency Pricing Support",
      portal: "Admin Portal",
      icon: <GlobalOutlined />,
      description:
        "QualifyMe now supports international pricing with automatic currency detection.",
      benefits: [
        "Automatic Region Detection: The platform automatically detects your region and displays prices in your local currency",
        "USD Support: International users outside India will see pricing in US Dollars",
        "INR Support: Users in India will continue to see pricing in Indian Rupees",
        "Consistent Experience: All pricing pages including Plans, Add-ons, and Cart Summary now reflect your local currency",
      ],
      howToAccess:
        "Simply visit the Pricing Plans page - your currency will be automatically detected based on your location.",
    },
    {
      id: 4,
      title: "Enterprise Plan Support",
      portal: "Admin Portal",
      icon: <RocketOutlined />,
      description:
        "Large organizations can now request custom Enterprise Plans tailored to their needs.",
      benefits: [
        "Custom Plan Requests: Contact our sales team for customized pricing and features",
        "Dedicated Contact Form: Fill out the enterprise inquiry form with your requirements",
        "Organization-Specific Plans: Receive a plan configured specifically for your organization",
      ],
      howToAccess: 'Click on "Enterprise" option on the Pricing Plans page.',
    },
    {
      id: 5,
      title: "Bulk Email Communication",
      portal: "Admin Portal",
      icon: <MailOutlined />,
      description: "Efficiently communicate with multiple candidates at once.",
      benefits: [
        "Select Multiple Candidates: Use checkboxes to select candidates across multiple pages",
        "Send Bulk Emails: Send customized emails to all selected candidates simultaneously",
        "Status-Based Filtering: Filter candidates by application status before sending emails",
        "Persistent Selections: Your candidate selections are now preserved when navigating between pages",
      ],
      howToAccess:
        'Go to any Job Post → Applications tab → Select candidates → Click "Send Email"',
      note: "The email button is only visible for active job posts.",
    },
    {
      id: 6,
      title: "Resume Upload by Admin",
      portal: "Admin Portal",
      icon: <FileTextOutlined />,
      description:
        "Administrators can now upload or re-upload resumes on behalf of candidates.",
      benefits: [
        "Candidate needs their resume to be updated",
        "Resume was not properly uploaded during application",
      ],
      howToAccess:
        "Go to Application Details → Profile Match section → Upload Resume",
    },
    {
      id: 7,
      title: "Application Rejection Reasons",
      portal: "Admin Portal",
      icon: <FileTextOutlined />,
      description:
        "Better visibility into why candidates were rejected from the hiring process.",
      benefits: [
        'Rejection Reason Labels: Clear labels showing the reason for rejection (e.g., "Profile Match Failure", "Assessment Not Cleared")',
        "Summary View: Rejection reasons are visible in the application summary",
        "Action Buttons Context: Relevant context is shown based on rejection status",
      ],
    },
    {
      id: 8,
      title: "AI Assessment Template Generator Enhancements",
      portal: "Admin Portal",
      icon: <SettingOutlined />,
      description:
        "The custom assessment creation experience has been significantly improved.",
      benefits: [
        "Skills Extraction: AI now automatically extracts relevant skills from job descriptions",
        "Custom Skills: Add your own custom skills beyond the AI-suggested ones",
        "Question Type Synchronization: Changes to question type are now synchronized across forms",
        "Template Generation Status: Clear progress indicators during template generation",
        'Expert Difficulty Level: New "Expert" difficulty option for advanced assessments',
      ],
      howToAccess:
        "When creating a Job Post → Step 3 (Assessments) → Create Custom Assessment",
    },
    {
      id: 9,
      title: "Enhanced Assessment Monitoring",
      portal: "Candidate Portal",
      icon: <VideoCameraOutlined />,
      description: "New proctoring capabilities ensure assessment integrity.",
      subFeatures: [
        {
          title: "Camera Obstruction Detection",
          items: [
            "Real-time Detection: System detects when the camera is being covered or obstructed",
            "Activity Logging: All obstruction events are recorded with timestamps",
            "Violation Display: Obstructions appear in the candidate's activity log",
          ],
        },
        {
          title: "System Audio Capture",
          items: [
            "Screen Recording Audio: For AI assessments requiring screen sharing, system audio is now captured",
            "Permission Handling: Clear prompts for system audio permissions",
            "Notification Alerts: Users are notified if system audio sharing is required but not granted",
          ],
        },
        {
          title: "Improved Violation Tracking",
          items: [
            "Better Activity Log: Enhanced display of all assessment violations",
            "Timestamp Sorting: Violations are sorted chronologically for easy review",
            "New Event Types: Added tracking for missed pings, session expirations, fullscreen exit attempts",
          ],
        },
      ],
    },
    {
      id: 10,
      title: "Assessment Duration Display",
      portal: "Candidate Portal",
      icon: <ClockCircleOutlined />,
      description:
        "Candidates now have better visibility into assessment timing.",
      benefits: [
        "Estimated Duration: The expected time to complete the assessment is displayed before starting",
        "Session Time: Clear indication of how long the assessment should take",
        "Completion Time: Time taken is tracked and displayed after completion",
      ],
    },
    {
      id: 11,
      title: "Improved User Interface & Experience",
      portal: "Both Portals",
      icon: <AppstoreOutlined />,
      description: "Several UI/UX improvements across the platform.",
      subFeatures: [
        {
          title: "Admin Portal",
          items: [
            '"Interview Rounds" is now called "Assessments" throughout the platform',
            "System automatically checks logo brightness for optimal display",
            "Better feedback messages during data loading and refresh operations",
            "Updated Progressive Web App icons and manifest for better mobile experience",
            "Contextual help section for free plan users to get started",
          ],
        },
        {
          title: "Candidate Portal",
          items: [
            "Improved color management using organization theme colors",
            "Enhanced responsiveness for AI assessment questions",
            "Improved instruction pages with duration estimates",
            "Updated success and error messages for clarity",
          ],
        },
      ],
    },
    {
      id: 12,
      title: "ATS Integration Improvements",
      portal: "Background",
      icon: <SyncOutlined />,
      description: "Improvements to Applicant Tracking System integrations.",
      benefits: [
        "Zoho Recruit: Enhanced feedback delivery with evaluation report attachments",
        "Lever Hire: Improved job post slug handling for better URL structure",
        "Error Handling: Better error messages when candidates already have applications",
      ],
    },
  ],
  bugFixes: [
    "Candidate selections now persist when navigating between pages",
    "Profile match status now displays correctly after evaluation",
    "Assessment configuration form now renders properly",
    "Email scheduling links now work correctly",
    "Activity log entries are now sorted in the correct order",
    "Various stability improvements throughout the platform",
  ],
  terminologyChanges: [
    { old: "Interview Rounds", new: "Assessments" },
    { old: "Interview Round", new: "Assessment" },
    { old: "HR Screening", new: "HR Screening (unchanged)" },
  ],
  systemRequirements: {
    browsers: [
      "Google Chrome (latest)",
      "Microsoft Edge (latest)",
      "Mozilla Firefox (latest)",
      "Safari (latest)",
    ],
    aiAssessments: [
      "Webcam access required",
      "Microphone access required",
      "Screen sharing capability (for proctored assessments)",
      "System audio sharing (for certain assessment types)",
    ],
  },
};

// Version 2.0.2 Content
const v202Content: ReleaseNoteContent = {
  version: "2.0.2",
  releaseDate: "December 24, 2025",
  previousVersion: "2.0.0 (December 3, 2025)",
  overview:
    "QualifyMe v2.0.2 adds powerful ATS integrations with Lever and Zoho Recruit, customizable interview rules, a new Partially Qualified status for nuanced candidate evaluation, role-based job visibility controls, and major performance and UI improvements across the platform.",
  features: [
    {
      id: 1,
      title: "Lever ATS Integration",
      portal: "Admin Portal",
      icon: <SyncOutlined />,
      description:
        "Seamlessly connect QualifyMe with your Lever ATS for streamlined hiring workflows.",
      benefits: [
        "Automatic Job Sync: Job posts from Lever are automatically synced to QualifyMe",
        "Candidate Flow: Candidates applying through Lever are automatically processed in QualifyMe",
        "Status Updates: Assessment results and candidate statuses are synced back to Lever",
        "OAuth Authentication: Secure connection using OAuth 2.0 protocol",
      ],
      howToAccess:
        "Go to Organization Settings → Integration → ATS → Connect Lever",
    },
    {
      id: 2,
      title: "Zoho Recruit Integration",
      portal: "Admin Portal",
      icon: <SyncOutlined />,
      description:
        "Connect QualifyMe with Zoho Recruit for automated candidate screening and assessment.",
      benefits: [
        "Bi-directional Sync: Jobs and candidates sync between Zoho Recruit and QualifyMe",
        "Automatic Screening: Candidates from Zoho Recruit are automatically screened",
        "Feedback Reports: Evaluation reports are automatically uploaded to candidate records",
        "Custom Field Mapping: Map QualifyMe fields to your Zoho Recruit custom fields",
      ],
      howToAccess:
        "Go to Organization Settings → Integration → ATS → Connect Zoho Recruit",
    },
    {
      id: 3,
      title: "Customizable Interview Rules",
      portal: "Admin Portal",
      icon: <SettingOutlined />,
      description:
        "Define custom rules for each interview round to control candidate behavior during assessments.",
      benefits: [
        "Tab Switching Rules: Configure whether tab switching is allowed or restricted",
        "Fullscreen Requirements: Set fullscreen mode requirements per round",
        "Camera Requirements: Control webcam requirements for different assessment types",
        "Time Limits: Set custom time limits for each interview round",
        "Attempt Limits: Configure maximum attempts allowed for assessments",
      ],
      howToAccess:
        "When creating/editing a Job Post → Assessment Configuration → Interview Rules",
    },
    {
      id: 4,
      title: "Partially Qualified Status",
      portal: "Admin Portal",
      icon: <CheckSquareOutlined />,
      description:
        'A new "Partially Qualified" status allows for more nuanced candidate evaluation.',
      benefits: [
        "Nuanced Evaluation: Candidates who meet some but not all criteria can be flagged for review",
        "Score Thresholds: Configure minimum scores for partial qualification",
        "Manual Override: Recruiters can manually change status based on assessment",
        "Better Pipeline Management: Identify promising candidates who may need additional evaluation",
      ],
      howToAccess:
        "View candidate application → Status is automatically assigned based on assessment results",
    },
    {
      id: 5,
      title: "Role-Based Job Visibility",
      portal: "Admin Portal",
      icon: <EyeOutlined />,
      description:
        "Control which team members can view and manage specific job posts.",
      benefits: [
        "Departmental Access: Restrict job visibility to relevant departments",
        "Interviewer Assignment: Only assigned interviewers can access candidate details",
        "Admin Override: Organization admins retain full access to all jobs",
        "Flexible Permissions: Configure visibility at the job post level",
      ],
      howToAccess:
        "When creating/editing a Job Post → Visibility Settings → Select team members",
    },
    {
      id: 6,
      title: "Enhanced Candidate Filtering",
      portal: "Admin Portal",
      icon: <FilterOutlined />,
      description:
        "Advanced filtering options for managing large candidate pools efficiently.",
      benefits: [
        "Multi-Status Filter: Filter by multiple application statuses simultaneously",
        "Score Range Filter: Filter candidates by assessment score ranges",
        "Date Range Filter: Filter applications by submission date",
        "Combined Filters: Apply multiple filters together for precise results",
      ],
      howToAccess: "Job Post Details → Applications tab → Filter menu",
    },
    {
      id: 7,
      title: "Interview Scheduling Improvements",
      portal: "Admin Portal",
      icon: <CalendarOutlined />,
      description:
        "Enhanced scheduling features for smoother interview coordination.",
      benefits: [
        "Calendar Integration: Sync with Google Calendar for availability",
        "Automated Reminders: Send automatic reminders to candidates and interviewers",
        "Timezone Support: Handle scheduling across different timezones",
        "Reschedule Options: Easy rescheduling with candidate notification",
      ],
      howToAccess:
        "Application Details → Schedule Interview → Select time slot",
    },
    {
      id: 8,
      title: "Improved Assessment Analytics",
      portal: "Admin Portal",
      icon: <BarChartOutlined />,
      description:
        "Better insights into assessment performance and candidate quality.",
      benefits: [
        "Score Distribution: View score distribution across all candidates",
        "Time Analysis: Analyze time taken by candidates for each section",
        "Completion Rates: Track assessment completion rates",
        "Question Performance: Identify which questions candidates struggle with",
      ],
      howToAccess: "Job Post Details → Analytics tab",
    },
    {
      id: 9,
      title: "Candidate Portal Improvements",
      portal: "Candidate Portal",
      icon: <UserOutlined />,
      description:
        "Enhanced experience for candidates taking assessments.",
      subFeatures: [
        {
          title: "Assessment Experience",
          items: [
            "Progress Indicator: Clear progress bar showing assessment completion",
            "Timer Display: Visible countdown timer with warnings",
            "Save Progress: Auto-save feature prevents data loss",
            "Better Instructions: Clearer instructions for each assessment type",
          ],
        },
        {
          title: "Dashboard Updates",
          items: [
            "Application Status: Real-time status updates for applications",
            "Next Steps: Clear guidance on what to do next",
            "Document Upload: Easier document upload interface",
            "Profile Management: Better profile editing experience",
          ],
        },
      ],
    },
    {
      id: 10,
      title: "Performance Optimizations",
      portal: "Both Portals",
      icon: <ThunderboltOutlined />,
      description:
        "Significant performance improvements across the platform.",
      benefits: [
        "Faster Page Loads: 40% improvement in initial page load times",
        "Optimized API Calls: Reduced API calls through better caching",
        "Lazy Loading: Components load on demand for faster initial render",
        "Database Optimization: Improved query performance for large datasets",
      ],
    },
    {
      id: 11,
      title: "UI/UX Refinements",
      portal: "Both Portals",
      icon: <AppstoreOutlined />,
      description:
        "Visual and usability improvements throughout the application.",
      benefits: [
        "Consistent Design: Unified design language across all pages",
        "Better Navigation: Improved sidebar and breadcrumb navigation",
        "Responsive Tables: Better table display on smaller screens",
        "Loading States: Improved loading indicators and skeleton screens",
        "Error Messages: Clearer error messages with actionable guidance",
      ],
    },
  ],
  bugFixes: [
    "Assessment timer now displays the correct remaining time",
    "Photo uploads now work with all common image formats",
    "Email notifications are now sent reliably for rescheduled interviews",
    "Assessment scores are now calculated accurately",
    "Candidate list pagination works smoothly",
    "Google Calendar sync now works with all event types",
    "Candidate status updates correctly after manual review",
    "Evaluation reports export properly to PDF",
    "Search now finds candidates with partial name matches",
  ],
  systemRequirements: {
    browsers: [
      "Google Chrome (latest)",
      "Microsoft Edge (latest)",
      "Mozilla Firefox (latest)",
      "Safari (latest)",
    ],
    aiAssessments: [
      "Webcam access required",
      "Microphone access required",
      "Screen sharing capability (for proctored assessments)",
      "Stable internet connection (minimum 2 Mbps)",
    ],
  },
};

// Version 2.0.0 Content
const v200Content: ReleaseNoteContent = {
  version: "2.0.0",
  releaseDate: "December 3, 2025",
  previousVersion: "1.9 (Previous major release)",
  overview:
    "QualifyMe v2.0.0 is a major release that introduces a completely redesigned job creation flow, smarter AI-powered assessments, enhanced resume analysis tools, faster performance, and over 25 new features. This release represents a significant milestone with major reliability improvements across both Admin and Candidate portals.",
  features: [
    {
      id: 1,
      title: "Redesigned Job Creation Flow",
      portal: "Admin Portal",
      icon: <EditOutlined />,
      description:
        "A completely new, streamlined 6-step job creation wizard that makes posting jobs faster and more intuitive.",
      subFeatures: [
        {
          title: "Step-by-Step Wizard",
          items: [
            "Step 1: Basic Details - Job title, position, openings, and compensation",
            "Step 2: Requirements - Skills, experience, and qualifications",
            "Step 3: Assessments - Configure AI assessments and interview rounds",
            "Step 4: HR Screening - Set up initial screening questions",
            "Step 5: Interviewers - Assign team members and guests",
            "Step 6: Review - Preview and publish your job post",
          ],
        },
        {
          title: "New Features",
          items: [
            "AI Job Description Generator: Generate professional descriptions from basic inputs",
            "Template Library: Save and reuse job post configurations",
            "Draft Auto-Save: Never lose your progress with automatic saving",
            "Preview Mode: See how your job post will appear to candidates",
          ],
        },
      ],
      howToAccess: 'Dashboard → Click "Create Job Post" button',
    },
    {
      id: 2,
      title: "AI-Powered Assessments",
      portal: "Admin Portal",
      icon: <RocketOutlined />,
      description:
        "Next-generation AI assessments that adapt to candidate responses and provide deeper insights.",
      benefits: [
        "Adaptive Questions: AI adjusts question difficulty based on candidate performance",
        "Multi-Format Support: MCQ, coding, video responses, and written answers",
        "Automated Evaluation: AI-powered scoring for subjective responses",
        "Skill Mapping: Questions automatically mapped to required skills",
        "Plagiarism Detection: Built-in plagiarism checking for written responses",
        "Custom Question Banks: Create and manage your own question repositories",
      ],
      howToAccess: "Job Post Creation → Step 3 (Assessments)",
    },
    {
      id: 3,
      title: "Enhanced Profile Matching",
      portal: "Admin Portal",
      icon: <SearchOutlined />,
      description:
        "Advanced resume analysis with AI-powered profile matching against job requirements.",
      benefits: [
        "Smart Parsing: Extracts skills, experience, and qualifications from resumes",
        "Match Scoring: Percentage-based matching against job requirements",
        "Gap Analysis: Identifies missing skills or experience",
        "Bulk Processing: Process multiple resumes simultaneously",
        "Match Insights: Detailed breakdown of why candidates match or don't",
      ],
      howToAccess: "Application Details → Profile Match section",
    },
    {
      id: 4,
      title: "Team Collaboration Features",
      portal: "Admin Portal",
      icon: <TeamOutlined />,
      description:
        "Enhanced tools for team-based hiring decisions.",
      benefits: [
        "Shared Notes: Add and view notes on candidate profiles",
        "Rating System: Rate candidates on multiple dimensions",
        "Interview Feedback: Structured feedback forms for interviewers",
        "Decision Tracking: Track hiring decisions and approvals",
        "Activity Log: See all team activities on a candidate",
      ],
      howToAccess: "Application Details → Team Collaboration section",
    },
    {
      id: 5,
      title: "Advanced Proctoring",
      portal: "Candidate Portal",
      icon: <VideoCameraOutlined />,
      description:
        "Comprehensive proctoring features to ensure assessment integrity.",
      subFeatures: [
        {
          title: "Monitoring Features",
          items: [
            "Face Detection: Ensures candidate is present and visible",
            "Tab Switch Detection: Monitors for browser tab switches",
            "Fullscreen Enforcement: Prevents window switching",
            "Audio Monitoring: Detects unusual audio patterns",
            "Screen Recording: Optional screen capture during assessments",
          ],
        },
        {
          title: "Violation Handling",
          items: [
            "Warning System: Candidates receive warnings before disqualification",
            "Violation Log: Detailed log of all detected violations",
            "Reviewer Dashboard: Admins can review flagged assessments",
            "Appeal Process: Candidates can explain flagged violations",
          ],
        },
      ],
    },
    {
      id: 6,
      title: "Subscription & Billing",
      portal: "Admin Portal",
      icon: <DatabaseOutlined />,
      description:
        "New subscription management and billing system.",
      benefits: [
        "Multiple Plans: Free, Pro, and Enterprise tiers",
        "Add-ons: Purchase additional features as needed",
        "Usage Tracking: Monitor your plan usage and limits",
        "Invoice Management: Access and download invoices",
        "Automatic Renewals: Seamless subscription renewals",
      ],
      howToAccess: "Sidebar → Pricing Plans",
    },
    {
      id: 7,
      title: "Email Communications",
      portal: "Admin Portal",
      icon: <MailOutlined />,
      description:
        "Comprehensive email system for candidate communications.",
      benefits: [
        "Email Templates: Pre-built templates for common scenarios",
        "Custom Templates: Create your own branded email templates",
        "Scheduling: Schedule emails for future delivery",
        "Tracking: Track email opens and clicks",
        "Bulk Sending: Send emails to multiple candidates",
      ],
      howToAccess: "Application Details → Send Email button",
    },
    {
      id: 8,
      title: "Dashboard & Analytics",
      portal: "Admin Portal",
      icon: <BarChartOutlined />,
      description:
        "Comprehensive dashboard with hiring metrics and analytics.",
      benefits: [
        "Hiring Pipeline: Visual representation of candidate flow",
        "Time-to-Hire: Track average time to fill positions",
        "Source Analytics: See where your best candidates come from",
        "Assessment Metrics: Analyze assessment completion and pass rates",
        "Team Performance: Track interviewer activity and feedback",
      ],
      howToAccess: "Dashboard (Home page)",
    },
    {
      id: 9,
      title: "Candidate Self-Service Portal",
      portal: "Candidate Portal",
      icon: <UserOutlined />,
      description:
        "Improved candidate experience with self-service features.",
      benefits: [
        "Application Tracking: Track status of all applications",
        "Document Management: Upload and manage documents",
        "Profile Building: Create comprehensive candidate profiles",
        "Interview Scheduling: View and manage interview schedules",
        "Notification Center: Stay updated on application progress",
      ],
    },
    {
      id: 10,
      title: "Mobile Responsiveness",
      portal: "Both Portals",
      icon: <MobileOutlined />,
      description:
        "Fully responsive design for mobile and tablet devices.",
      benefits: [
        "Mobile-First Design: Optimized layouts for smaller screens",
        "Touch-Friendly: Easy navigation with touch gestures",
        "PWA Support: Install as a Progressive Web App",
        "Offline Access: View cached content without internet",
      ],
    },
    {
      id: 11,
      title: "Security Enhancements",
      portal: "Both Portals",
      icon: <LockOutlined />,
      description:
        "Enhanced security features to protect your data.",
      benefits: [
        "Two-Factor Authentication: Optional 2FA for admin accounts",
        "Session Management: Control active sessions",
        "Audit Logs: Track all security-related events",
        "Data Encryption: End-to-end encryption for sensitive data",
        "GDPR Compliance: Tools for data privacy compliance",
      ],
    },
    {
      id: 12,
      title: "Notification System",
      portal: "Both Portals",
      icon: <BellOutlined />,
      description:
        "Comprehensive notification system to keep everyone informed.",
      benefits: [
        "In-App Notifications: Real-time notifications within the platform",
        "Email Alerts: Configurable email notifications",
        "Notification Preferences: Control what notifications you receive",
        "Read/Unread Status: Track which notifications you've seen",
      ],
    },
  ],
  bugFixes: [
    "Improved login reliability and session management",
    "Better performance on the candidate portal",
    "Large video files now upload successfully",
    "Dates and times display correctly across all timezones",
    "Evaluation reports generate properly as PDF",
    "Faster and more accurate candidate search",
    "Assessment submissions now process reliably",
    "Emails now deliver to all email providers",
    "Assessment scores calculate correctly",
  ],
  systemRequirements: {
    browsers: [
      "Google Chrome 90+ (Recommended)",
      "Microsoft Edge 90+",
      "Mozilla Firefox 88+",
      "Safari 14+",
    ],
    aiAssessments: [
      "Webcam access required",
      "Microphone access required",
      "Minimum screen resolution: 1280x720",
      "Stable internet connection (minimum 2 Mbps)",
      "JavaScript enabled",
    ],
  },
  knownIssues: [
    "Video assessments may experience slight delays on slower connections",
    "Some older Safari versions may have issues with screen recording",
  ],
};

// Version 2.0.4 Content
const v204Content: ReleaseNoteContent = {
  version: "2.0.4",
  releaseDate: "February 10, 2026",
  previousVersion: "2.0.3 (January 13, 2026)",
  overview:
    "QualifyMe v2.0.4 introduces error handling enhancements that make the platform more user-friendly and reliable. This release focuses on clearer error messages, improved resume processing, better file handling, and enhanced assessment expiration management to reduce candidate confusion and improve the overall experience.",
  features: [
    {
      id: 1,
      title: "Improved Error Messages",
      portal: "Admin & Candidate Portals",
      icon: <BellOutlined />,
      description:
        "The platform now provides clear, actionable error messages when something goes wrong during resume uploads or assessments.",
      benefits: [
        "Specific Error Codes: Each error is identified with a unique code so support can help more quickly",
        "Clear Guidance: Instead of generic 'Something went wrong', candidates see exactly what needs to be fixed",
        "Recovery Steps: Error messages now include suggestions on how to resolve the issue",
        "Better Debugging: Support team can quickly identify and resolve issues with detailed error codes",
      ],
      howToAccess:
        "Error messages appear automatically when an issue occurs during file upload or assessment",
    },
    {
      id: 2,
      title: "Enhanced Resume Upload Handling",
      portal: "Admin & Candidate Portals",
      icon: <FileTextOutlined />,
      description:
        "Resume uploads are now more robust with better detection of file format issues.",
      benefits: [
        "Image-Only PDF Detection: Automatically detects if a resume is a scan without readable text",
        "Empty File Detection: Identifies empty or corrupted PDF files before processing",
        "Complex Format Detection: Detects unusually complex layouts that may not parse correctly",
        "Language Support: Clearly indicates if a resume is not in English",
      ],
      howToAccess: "Upload a resume in the Admin Portal or Candidate Portal",
    },
    {
      id: 3,
      title: "Assessment Expiration Management",
      portal: "Admin Portal",
      icon: <ClockCircleOutlined />,
      description:
        "Admins can now set expiration dates for assessments, and candidates are notified when assessments are about to expire.",
      benefits: [
        "Configurable Expiry Dates: Set how long candidates have to complete assessments",
        "Email Notifications: Candidates receive timely reminders before assessments expire",
        "Expired Assessment Handling: System automatically marks assessments as expired when deadline passes",
        "Better Planning: Admins can manage assessment windows more effectively",
      ],
      howToAccess:
        "When creating a Job Post → Step 3 (Assessments) → Set Interview Round Expiry duration",
    },
    {
      id: 4,
      title: "Central Notification Service",
      portal: "Background",
      icon: <SyncOutlined />,
      description:
        "A new unified notification system ensures all candidates receive consistent, timely updates.",
      benefits: [
        "Reliable Delivery: Notifications are delivered through a centralized system",
        "Job Application Updates: Candidates are notified of status changes in their applications",
        "Interview Scheduling: Automatic notifications for interview invitations and reminders",
        "Assessment Events: Notifications for assessment assignments and expirations",
      ],
      howToAccess:
        "Notifications are automatically sent based on system events - no configuration needed",
    },
    {
      id: 5,
      title: "Enhanced Bulk Upload Processing",
      portal: "Admin Portal",
      icon: <MailOutlined />,
      description:
        "Bulk resume uploads now provide detailed feedback on which resumes processed successfully and which failed.",
      benefits: [
        "Progress Tracking: See real-time progress as files are being processed",
        "Detailed Error Reporting: Each failed file shows why it couldn't be processed",
        "Success Summary: Clear count of successful vs. failed uploads",
        "Retry Capability: Easily resubmit failed resumes without re-uploading all files",
      ],
      howToAccess:
        "Job Post Applications tab → Bulk Upload → Track progress and review results",
    },
    {
      id: 6,
      title: "Improved UI/UX for Error States",
      portal: "Both Portals",
      icon: <AppstoreOutlined />,
      description: "User interface improvements to handle and display errors more gracefully.",
      benefits: [
        "Visual Feedback: Loading states are clearer and more informative",
        "Better Error Display: Errors are highlighted and made easy to understand",
        "Contextual Help: Additional guidance is provided when errors occur",
        "Consistent Design: Error handling follows the same design patterns across the app",
      ],
      howToAccess: "Visible throughout the application when processing files or handling issues",
    },
  ],
  bugFixes: [
    "Fixed resume parsing failures for image-only PDFs",
    "Improved handling of corrupted or empty PDF files",
    "Fixed assessment expiration notifications not being sent",
    "Corrected bulk upload error reporting accuracy",
    "Fixed loading spinner appearing unexpectedly during processing",
    "Improved authentication error handling to prevent multiple redirect attempts",
    "Better handling of unsupported file formats with clear error messages",
  ],
  systemRequirements: {
    browsers: [
      "Google Chrome (latest)",
      "Microsoft Edge (latest)",
      "Mozilla Firefox (latest)",
      "Safari (latest)",
    ],
    aiAssessments: [
      "Webcam access required",
      "Microphone access required",
      "Screen sharing capability (for proctored assessments)",
      "System audio sharing (for certain assessment types)",
    ],
  },
};

// Version 2.0.5 Content
const v205Content: ReleaseNoteContent = {
  version: "2.0.5",
  releaseDate: "February 24, 2026",
  previousVersion: "2.0.4 (February 10, 2026)",
  overview:
    "QualifyMe v2.0.5 brings significant enhancements to the candidate experience and introduces optional profile matching capabilities. This release focuses on streamlining the hiring process with free profile screening, improved onboarding for candidates, and better control over hiring workflows.",
  features: [
    {
      id: 1,
      title: "Free Profile Screening",
      portal: "Candidate Portal",
      icon: <SearchOutlined />,
      description:
        "A new public screening feature allows candidates to evaluate their fit for a job before formally applying.",
      benefits: [
        "Self-Assessment Tool: Candidates can check their profile match before committing to apply",
        "Reduced Unqualified Applications: Better matching leads to fewer misaligned applications",
        "Transparency: Candidates understand upfront whether they meet the job requirements",
        "No Login Required: Public screening is accessible without creating an account",
      ],
      howToAccess:
        "Share the public screening link with candidates - no admin setup required",
    },
    {
      id: 2,
      title: "Optional Profile Matching",
      portal: "Admin Portal",
      icon: <CheckSquareOutlined />,
      description:
        "Admins can now make profile matching optional for certain jobs or disable it entirely if not needed.",
      benefits: [
        "Flexible Workflows: Choose whether profile matching is required for each job",
        "Faster Hiring: Skip profile matching for positions where it's not needed",
        "Custom Requirements: Different jobs can have different matching requirements",
        "Better for Internal Transfers: Disable matching for jobs where qualifications are flexible",
      ],
      howToAccess:
        "Job Post Creation → Step 2 (Requirements) → Toggle 'Require Profile Match'",
    },
    {
      id: 3,
      title: "Enhanced User Onboarding",
      portal: "Admin Portal",
      icon: <RocketOutlined />,
      description:
        "Improved onboarding experience for new recruiters getting started with job creation.",
      benefits: [
        "Step-by-Step Guidance: Clear navigation through the job creation process",
        "Contextual Help: Tips and hints at each step to guide users",
        "Data Pre-Filling: Previously entered data is remembered for faster job creation",
        "Progress Tracking: Clear indication of which steps are complete",
      ],
      howToAccess: "Create a new job post - the improved wizard will guide you through each step",
    },
    {
      id: 4,
      title: "Improved Job Post Listing",
      portal: "Admin Portal",
      icon: <FileTextOutlined />,
      description:
        "Better organization and filtering of job posts on the admin dashboard.",
      benefits: [
        "My Job Posts vs. All Posts: Separate views for your jobs and team jobs",
        "Better Filtering: More sorting and filtering options",
        "Clear Status Indicators: Easy to see which jobs are active or archived",
        "Quick Actions: Faster access to commonly used job management features",
      ],
      howToAccess: "Go to Dashboard → My Job Posts or All Job Posts",
    },
    {
      id: 5,
      title: "Enhanced AI Job Description Generation",
      portal: "Admin Portal",
      icon: <RocketOutlined />,
      description:
        "Improved AI-powered job description and requirements generation with better error handling.",
      benefits: [
        "Better Content: AI generates more relevant and complete job descriptions",
        "Error Feedback: Clear messages if generation fails with suggestions to fix it",
        "Custom Hooks: Recruiters can provide specific inputs to customize generated content",
        "Faster Creation: Generates high-quality descriptions in seconds",
      ],
      howToAccess:
        "Job Post Creation → Step 1 → Use 'Generate with AI' button for descriptions",
    },
    {
      id: 6,
      title: "Improved Resume Processing in Onboarding",
      portal: "Admin Portal",
      icon: <FileTextOutlined />,
      description:
        "More streamlined resume processing as part of the job creation workflow.",
      benefits: [
        "Integrated Flow: Upload resumes directly during job post creation",
        "Batch Processing: Process multiple resumes in one workflow",
        "Better Feedback: Clear indication of processing status and results",
        "Error Recovery: Easy retry for failed resumes",
      ],
      howToAccess: "During job post creation - resume upload is integrated into the wizard",
    },
    {
      id: 7,
      title: "Improved Data Separation",
      portal: "Admin Portal",
      icon: <DatabaseOutlined />,
      description:
        "Better organization of data to prevent confusion between different job posts.",
      benefits: [
        "Clearer Navigation: Easy to distinguish between different jobs and applications",
        "Better Filtering: More accurate search results across large datasets",
        "Reduced Errors: Less chance of mixing up data from different jobs",
        "Improved Performance: Faster loading with better-organized data",
      ],
      howToAccess: "Visible throughout the job management interface",
    },
    {
      id: 8,
      title: "Enhanced Theme Configuration",
      portal: "Admin Portal",
      icon: <SettingOutlined />,
      description:
        "Improved organization theme settings with better color and branding options.",
      benefits: [
        "SSO Enhancements: Better configuration for OAuth and SSO integration",
        "Automatic Logo Processing: System automatically optimizes logo display",
        "Better Color Management: More control over how organization colors appear",
        "Flexible Theme Options: More customization options for branding",
      ],
      howToAccess:
        "Organization Settings → Theme & Branding → Configure colors and logos",
    },
  ],
  bugFixes: [
    "Fixed authentication check appearing multiple times in the application",
    "Resolved issue with table heights on smaller screens",
    "Improved PDF size optimization for faster uploads",
    "Fixed navigation and data flow in onboarding wizard",
    "Corrected styling for job application interview rounds",
    "Fixed notification message wording for better clarity",
    "Improved error handling in SSO configuration",
  ],
  systemRequirements: {
    browsers: [
      "Google Chrome (latest)",
      "Microsoft Edge (latest)",
      "Mozilla Firefox (latest)",
      "Safari (latest)",
    ],
    aiAssessments: [
      "Webcam access required",
      "Microphone access required",
      "Screen sharing capability (for proctored assessments)",
      "System audio sharing (for certain assessment types)",
    ],
  },
};

// Version 2.0.6 Content
const v206Content: ReleaseNoteContent = {
  version: "2.0.6",
  releaseDate: "March 10, 2026",
  previousVersion: "2.0.5 (February 24, 2026)",
  overview:
    "QualifyMe v2.0.6 focuses on improving assessment management and candidate communication. This release introduces assessment rate limiting to prevent system abuse, an updated subscription management system, and enhanced analytics for better hiring insights.",
  features: [
    {
      id: 1,
      title: "Assessment Rate Limiting",
      portal: "Admin Portal",
      icon: <ThunderboltOutlined />,
      description:
        "New controls to prevent excessive assessment usage and manage subscription quotas more effectively.",
      benefits: [
        "Quota Management: Track how many assessments you've used this billing period",
        "Rate Limit Warnings: Get notified when approaching your assessment limits",
        "Automatic Enforcement: System prevents exceeding plan limits",
        "Clear Tracking: Dashboard shows current usage vs. limit",
      ],
      howToAccess: "Dashboard → Subscription & Usage → View assessment quotas",
    },
    {
      id: 2,
      title: "Subscription Limit Warnings",
      portal: "Admin Portal",
      icon: <BellOutlined />,
      description:
        "Proactive notifications about reaching subscription limits before they impact your hiring process.",
      benefits: [
        "Daily Checks: System checks your usage daily and alerts you",
        "Email Notifications: Get alerted via email when limits are approaching",
        "Plan Recommendations: Suggestions on upgrading if you're outgrowing your plan",
        "Usage Analytics: Clear breakdown of how you're using your subscription",
      ],
      howToAccess:
        "Notifications appear in the admin dashboard and email notifications",
    },
    {
      id: 3,
      title: "Assessment Completion Tracking",
      portal: "Admin Portal",
      icon: <CheckSquareOutlined />,
      description:
        "Better visibility into which candidates have completed or skipped assessments.",
      benefits: [
        "Completion Status: Clear indicator of assessment completion status",
        "Expiration Tracking: See which assessments have expired",
        "Candidate Reminders: Automatic reminders for pending assessments",
        "Completion Analytics: Track completion rates by job or assessment type",
      ],
      howToAccess: "Job Post → Applications tab → Assessment Status column",
    },
    {
      id: 4,
      title: "Candidate Portal Improvements",
      portal: "Candidate Portal",
      icon: <UserOutlined />,
      description:
        "Enhanced candidate experience with better navigation and clearer application tracking.",
      benefits: [
        "Application Overview: Clear dashboard showing all your applications",
        "Status Updates: Real-time updates on application progress",
        "Next Steps: Clear guidance on what to do next",
        "Document Management: Easier access to upload additional documents",
      ],
      howToAccess: "Login to candidate portal to see improved dashboard",
    },
    {
      id: 5,
      title: "Improved Notification Payload Structure",
      portal: "Background",
      icon: <SyncOutlined />,
      description:
        "Better structured notification data for more reliable integration with external systems.",
      benefits: [
        "Consistent Format: Notifications follow a standard format for easier parsing",
        "Complete Information: All relevant details included in notifications",
        "Better ATS Sync: More reliable integration with Applicant Tracking Systems",
        "External Integration: Easier to build custom integrations with the notification system",
      ],
      howToAccess: "Automatically handled by the system - no manual configuration",
    },
    {
      id: 6,
      title: "Enhanced Analytics Dashboard",
      portal: "Admin Portal",
      icon: <BarChartOutlined />,
      description:
        "More comprehensive hiring metrics and insights to track recruitment effectiveness.",
      benefits: [
        "Assessment Performance: See how candidates perform on different assessments",
        "Time-to-Hire Metrics: Track how long it takes to fill positions",
        "Candidate Source Analytics: Understand where your best candidates come from",
        "Team Performance: Track interviewer activity and feedback quality",
      ],
      howToAccess: "Dashboard → Analytics tab",
    },
    {
      id: 7,
      title: "Improved ATS Integration Reliability",
      portal: "Background",
      icon: <SyncOutlined />,
      description:
        "More robust integration with ATS systems to ensure data stays synchronized.",
      benefits: [
        "Better Error Handling: Clear reporting when syncs fail",
        "Automatic Retries: System automatically retries failed syncs",
        "Status Tracking: See sync status for each integration",
        "Data Integrity: Better validation to prevent data mismatches",
      ],
      howToAccess:
        "Organization Settings → Integration → ATS → View sync status",
    },
    {
      id: 8,
      title: "Enhanced API Documentation",
      portal: "Admin Portal",
      icon: <ApiOutlined />,
      description:
        "Improved API documentation and examples for developers building integrations.",
      benefits: [
        "Clearer Examples: Real-world code samples for common integrations",
        "Better Error Codes: Documented error responses for better troubleshooting",
        "Updated Schemas: Complete up-to-date API schema documentation",
        "Integration Guides: Step-by-step guides for popular platforms",
      ],
      howToAccess: "Organization Settings → Integration → API → View Documentation",
    },
  ],
  bugFixes: [
    "Fixed assessment expiry handling across all job posts",
    "Corrected bulk upload file path handling for better reliability",
    "Fixed candidate portal API to properly return active job posts",
    "Improved error handling in resume parsing service",
    "Fixed job application status updates in ATS integrations",
    "Corrected notification event type handling for consistency",
    "Fixed subscription limit checks to properly evaluate plan limits",
    "Improved session management in assessment processing",
  ],
  terminologyChanges: [
    {
      old: "Interview Round Expiration",
      new: "Assessment Expiration (more precise)",
    },
  ],
  systemRequirements: {
    browsers: [
      "Google Chrome (latest)",
      "Microsoft Edge (latest)",
      "Mozilla Firefox (latest)",
      "Safari (latest)",
    ],
    aiAssessments: [
      "Webcam access required",
      "Microphone access required",
      "Screen sharing capability (for proctored assessments)",
      "System audio sharing (for certain assessment types)",
    ],
  },
};

// Version 2.0.7 Content
const v207Content: ReleaseNoteContent = {
  version: "2.0.7",
  releaseDate: "March 25, 2026",
  previousVersion: "2.0.6 (March 10, 2026)",
  overview:
    "QualifyMe v2.0.7 introduces multi-channel candidate communication with WhatsApp notification support, bulk application management, assessment deadline controls, and improved team member management. This release also includes subscription GST handling for Indian customers, enhanced email configuration, and significant UI/UX improvements across both portals.",
  features: [
    {
      id: 1,
      title: "WhatsApp Notification Preferences",
      portal: "Admin Portal",
      icon: <BellOutlined />,
      description:
        "Admins can now configure candidate notification preferences per job post, allowing candidates to receive updates via email, WhatsApp, or both.",
      benefits: [
        "Multi-Channel Notifications: Reach candidates through their preferred communication channel",
        "Per-Job Configuration: Set notification preferences independently for each job post",
        "WhatsApp Reminders: Automated WhatsApp reminders for assessment deadlines and application updates",
        "Candidate Consent: Candidates control their WhatsApp opt-in preference during application",
        "Fallback Handling: Clear error messages when notification delivery fails",
      ],
      howToAccess:
        "Job Post Creation → Notification Settings → Select email and/or WhatsApp preferences",
    },
    {
      id: 2,
      title: "Bulk Actions for Job Applications",
      portal: "Admin Portal",
      icon: <CheckSquareOutlined />,
      description:
        "Perform bulk operations on multiple candidate applications simultaneously for more efficient pipeline management.",
      benefits: [
        "Select Multiple Candidates: Use checkboxes to select candidates across the list",
        "Bulk Status Updates: Change application status for multiple candidates at once",
        "Bulk Email: Send communications to all selected candidates simultaneously",
        "Efficient Workflow: Manage large applicant pools without repetitive individual actions",
      ],
      howToAccess:
        "Job Post → Applications tab → Select candidates using checkboxes → Choose bulk action",
    },
    {
      id: 3,
      title: "Assessment Deadline Management",
      portal: "Admin Portal",
      icon: <ClockCircleOutlined />,
      description:
        "Set and manage specific deadlines for each assessment round, with automated reminders sent to candidates before the deadline.",
      benefits: [
        "Per-Round Deadlines: Configure independent deadlines for each assessment round",
        "Automated Reminders: Candidates receive timely WhatsApp and email reminders before deadlines",
        "Deadline Enforcement: System automatically expires assessments past their deadline",
        "Visibility: Clear deadline indicators on candidate application cards",
      ],
      howToAccess:
        "Job Post Creation → Step 3 (Assessments) → Set deadline per assessment round",
    },
    {
      id: 4,
      title: "Profile Screening Page Improvements",
      portal: "Candidate Portal",
      icon: <SearchOutlined />,
      description:
        "The public profile screening page has been updated with refreshed content and full responsive design across all screen sizes.",
      benefits: [
        "Responsive Design: Optimized layout for mobile, tablet, and desktop screens",
        "Updated Content: Clearer messaging about the screening process and what to expect",
        "Better UX: Improved visual hierarchy and call-to-action placement",
        "Faster Load: Optimized page for quick loading on all devices",
      ],
      howToAccess: "Share the public screening link with candidates",
    },
    {
      id: 5,
      title: "Team Member Management Enhancements",
      portal: "Admin Portal",
      icon: <TeamOutlined />,
      description:
        "Improved team member features with better role assignment, visibility controls, and stability fixes.",
      benefits: [
        "Better Role Assignment: More reliable assignment of interviewers and reviewers to job posts",
        "Improved Visibility Controls: Fine-grained control over which team members can access specific jobs",
        "Stability Fixes: Resolved issues with team member list rendering and updates",
        "Consistent Behavior: Uniform experience when managing members across different job posts",
      ],
      howToAccess:
        "Job Post Creation → Step 5 (Interviewers) or Organization Settings → Team Members",
    },
    {
      id: 6,
      title: "Subscription GST Handling",
      portal: "Admin Portal",
      icon: <DatabaseOutlined />,
      description:
        "Indian customers now see GST-inclusive pricing and receive GST-compliant invoices for all subscription purchases.",
      benefits: [
        "GST-Inclusive Pricing: Transparent display of GST on all pricing pages",
        "Compliant Invoices: Invoices include GST breakdown as required by Indian tax regulations",
        "Automatic Detection: GST is applied automatically for Indian accounts",
        "Accurate Billing: Correct tax calculation for all plan and add-on purchases",
      ],
      howToAccess: "Pricing Plans or Billing & Invoices section",
    },
    {
      id: 7,
      title: "Email Configuration Settings",
      portal: "Admin Portal",
      icon: <MailOutlined />,
      description:
        "New email configuration options allow organizations to customize sender settings and notification templates.",
      benefits: [
        "Custom Sender Settings: Configure the display name and reply-to address for outgoing emails",
        "Template Management: View and manage email templates used for candidate communications",
        "Notification Control: Choose which events trigger email notifications",
        "Better Deliverability: Improved email infrastructure for more reliable delivery",
      ],
      howToAccess:
        "Organization Settings → Email Configuration",
    },
    {
      id: 8,
      title: "Analytics & Tracking Improvements",
      portal: "Background",
      icon: <BarChartOutlined />,
      description:
        "Analytics scripts have been refactored into a dedicated component with environment-aware initialization to prevent dev environment noise.",
      benefits: [
        "Dev Environment Guard: GTM, GA4, and LinkedIn Insight Tag no longer trigger in development builds",
        "Cleaner Architecture: Analytics moved from index.html to a dedicated component for better maintainability",
        "Accurate Data: Production analytics are unaffected while dev testing stays clean",
        "Easier Maintenance: Centralized analytics configuration makes future updates simpler",
      ],
      howToAccess: "Automatically handled — no configuration needed",
    },
  ],
  bugFixes: [
    "Fixed resume drawer display issues affecting candidate profile view",
    "Resolved job post form validation errors preventing submission",
    "Updated WhatsApp consent condition to correctly reflect candidate opt-in status",
    "Added proper error messages when the notification service fails",
    "Fixed analytics initialization condition (corrected !== to === for GTM)",
    "Resolved build issue introduced during analytics refactoring",
    "Improved error handling for resume processing edge cases",
    "Fixed team member assignment not persisting correctly across sessions",
  ],
  systemRequirements: {
    browsers: [
      "Google Chrome (latest)",
      "Microsoft Edge (latest)",
      "Mozilla Firefox (latest)",
      "Safari (latest)",
    ],
    aiAssessments: [
      "Webcam access required",
      "Microphone access required",
      "Screen sharing capability (for proctored assessments)",
      "System audio sharing (for certain assessment types)",
    ],
  },
};

// Version 2.0.8 Content
const v208Content: ReleaseNoteContent = {
  version: "2.0.8",
  releaseDate: "April 13, 2026",
  previousVersion: "2.0.7 (March 25, 2026)",
  overview:
    "QualifyMe v2.0.8 is a feature-rich release delivering AI-powered profile analysis, a significantly upgraded assessment engine (Assessments 2.0), Zoho Recruit pull-based integration, assessment remind-later functionality, and improved invite management. This release also strengthens platform reliability with better notification controls, profile match gating, and targeted bug fixes across all portals.",
  features: [
    {
      id: 1,
      title: "AI Profile Analysis",
      portal: "Admin Portal",
      icon: <SearchOutlined />,
      description:
        "A new AI-powered profile analysis feature provides deeper insights into candidate profiles, going beyond basic resume matching to surface nuanced fit signals.",
      benefits: [
        "Deeper Insights: AI evaluates candidate profiles against job requirements with greater accuracy",
        "Skill Gap Identification: Clearly highlights missing or partially matched skills",
        "Automated Scoring: Profile analysis scores are generated automatically on application",
        "Actionable Summary: Recruiters get a concise analysis summary without manual review",
      ],
      howToAccess: "Application Details → Profile Analysis section",
    },
    {
      id: 3,
      title: "Assessment Remind Later",
      portal: "Candidate Portal",
      icon: <ClockCircleOutlined />,
      description:
        "Candidates can now choose to be reminded later about pending assessments instead of having to start immediately.",
      benefits: [
        "Flexible Scheduling: Candidates can defer assessment notifications to a more convenient time",
        "Reduced Drop-offs: Fewer candidates abandon assessments due to poor timing",
        "Automated Follow-up: System sends reminders at the scheduled time automatically",
        "Better Completion Rates: More candidates complete assessments when they choose their timing",
      ],
      howToAccess:
        "Candidate Portal → Pending Assessment → Select 'Remind me later'",
    },
    {
      id: 4,
      title: "Assessment Expiry Violation Tracking",
      portal: "Admin Portal",
      icon: <ThunderboltOutlined />,
      description:
        "The platform now tracks and logs when candidates attempt to access or submit assessments after the expiry deadline.",
      benefits: [
        "Violation Logging: Expired assessment access attempts are recorded with timestamps",
        "Audit Trail: Recruiters can review expiry violation events in the candidate activity log",
        "Integrity Enforcement: System blocks submission of expired assessments with clear messaging",
        "Better Oversight: Helps identify candidates who repeatedly attempt expired assessments",
      ],
      howToAccess: "Application Details → Activity Log → Assessment Expiry events",
    },
    {
      id: 5,
      title: "Zoho Recruit Pull Integration",
      portal: "Background",
      icon: <SyncOutlined />,
      description:
        "QualifyMe can now actively pull candidate and job data from Zoho Recruit, enabling a more robust bi-directional sync with feedback delivery support.",
      benefits: [
        "Pull-Based Sync: QualifyMe proactively fetches candidates from Zoho Recruit on a schedule",
        "Feedback Delivery: Evaluation reports and assessment feedback are sent back to Zoho Recruit",
        "Latest ATS Types: Updated to support the latest Zoho Recruit candidate data structures",
        "Fewer Missed Candidates: Pull mechanism reduces gaps caused by missed webhook events",
      ],
      howToAccess:
        "Organization Settings → Integration → ATS → Zoho Recruit",
    },
    {
      id: 6,
      title: "Invite Management — Resend & Delete",
      portal: "Admin Portal",
      icon: <MailOutlined />,
      description:
        "Admins can now resend pending invitations and delete stale invites directly from the team management interface.",
      benefits: [
        "Resend Invites: Re-send invitation emails to team members who haven't accepted yet",
        "Delete Invites: Remove outdated or incorrect invitations without manual database intervention",
        "Better Control: Full lifecycle management of team member invitations",
        "Slot Notifications: Automatic notification is triggered when an interview slot is selected",
      ],
      howToAccess:
        "Organization Settings → Team Members → Pending Invites → Resend or Delete",
    },
    {
      id: 7,
      title: "Profile Match Gating for Hire & Shortlist",
      portal: "Admin Portal",
      icon: <LockOutlined />,
      description:
        "Recruiters are now prevented from marking candidates as Hired or Shortlisted while profile matching is still in progress, ensuring decisions are based on complete data.",
      benefits: [
        "Data Integrity: Prevents premature hiring decisions before profile analysis completes",
        "Clear Indicators: Hire and Shortlist actions are visibly disabled with an explanatory tooltip",
        "Consistent Workflow: Enforces a structured evaluation process across the team",
        "Reduced Errors: Fewer accidental status changes on unanalyzed profiles",
      ],
      howToAccess:
        "Application Details → Status actions are automatically gated when profile matching is pending",
    },
    {
      id: 8,
      title: "Notification Abuse Protection",
      portal: "Background",
      icon: <SettingOutlined />,
      description:
        "Email and WhatsApp notification sending can now be disabled at the environment level to prevent accidental mass messaging in non-production environments or during maintenance.",
      benefits: [
        "Environment Guard: A single .env flag disables all outbound notifications for dev/staging",
        "Abuse Prevention: Prevents accidental bulk sends during testing or misconfiguration",
        "Safe Maintenance: Safely perform bulk data operations without triggering candidate emails",
        "Granular Control: Email and WhatsApp can be toggled independently",
      ],
      howToAccess: "Managed via environment configuration — no UI action required",
    },
    {
      id: 9,
      title: "Tax Handling for Plans & Credits",
      portal: "Admin Portal",
      icon: <DatabaseOutlined />,
      description:
        "Tax is now calculated and displayed exclusively for plan upgrades and credit purchases, ensuring accurate billing for all subscription transactions.",
      benefits: [
        "Accurate Tax Display: Tax breakdown shown only on applicable purchase types",
        "Plan Purchases: Tax applied correctly when upgrading or renewing subscription plans",
        "Credit Purchases: Tax included when purchasing additional assessment credits",
        "Compliant Billing: Invoices reflect correct tax treatment per transaction type",
      ],
      howToAccess: "Pricing Plans → Checkout or Add-ons → Purchase credits",
    },
  ],
  bugFixes: [
    "Fixed WhatsApp consent not persisting correctly — moved from User to UserProfile model",
    "Fixed job application status update failures and incorrect mail template rendering",
    "Disabled marking candidates as Hired or Shortlisted while profile matching is still pending",
    "Added condition check before calling LinkedIn tracker to prevent errors on untracked events",
    "Fixed prompt output format parameter for compatibility with the latest LangChain version",
    "Fixed email skip logic during assessment processing edge cases",
    "Updated shortlisted event to correctly trigger downstream notifications",
    "Removed stale loading indicator from ApplicationJourneySteps component",
    "Various bug fixes and stability enhancements across admin and candidate portals",
  ],
  systemRequirements: {
    browsers: [
      "Google Chrome (latest)",
      "Microsoft Edge (latest)",
      "Mozilla Firefox (latest)",
      "Safari (latest)",
    ],
    aiAssessments: [
      "Webcam access required",
      "Microphone access required",
      "Screen sharing capability (for proctored assessments)",
      "System audio sharing (for certain assessment types)",
    ],
  },
};

// Version 2.0.9 Content
const v209Content: ReleaseNoteContent = {
  version: "2.0.9",
  releaseDate: "April 26, 2026",
  previousVersion: "2.0.8 (April 13, 2026)",
  overview:
    "QualifyMe v2.0.9 introduces the Candidate Job Fit Report giving recruiters a full pre and post assessment view of each candidate, a daily end-of-day email digest with pipeline stats, and a polished candidate portal with improved design consistency. This release also resolves Zoho Recruit sync issues, report download failures, and various UI bugs across both portals.",
  features: [
    {
      id: 1,
      title: "Candidate Job Fit Report",
      portal: "Admin Portal",
      icon: <FileTextOutlined />,
      description:
        "Recruiters can now download a Candidate Job Fit Report that provides a complete picture of how well a candidate matches the role, covering both pre and post assessment data.",
      benefits: [
        "Pre-Assessment Summary: Captures candidate profile, resume match, and screening details before the assessment",
        "Post-Assessment Summary: Includes evaluation scores, AI analysis, and violation summary after completion",
        "Updated Templates: Redesigned report templates for cleaner, more readable output",
        "One-Click Download: Download the full report directly from the application details page",
      ],
      howToAccess: "Application Details → Download Report button",
    },
    {
      id: 2,
      title: "End of Day Report for Recruiters",
      portal: "Admin Portal",
      icon: <MailOutlined />,
      description:
        "Recruiters now receive a daily email summary of hiring activity across their job posts, giving them a quick overview of pipeline progress without logging in.",
      benefits: [
        "Applications Received: Total number of new candidates who applied during the day",
        "Assessments Taken: Count of candidates who started or completed their assessment",
        "Rejected Candidates: Number of candidates rejected based on profile match or assessment results",
        "Qualified Candidates: Count of candidates who passed all required stages",
        "Pending Assessments: Number of candidates yet to complete their assigned assessment",
      ],
      note: "The daily report is sent automatically at the end of each day to all recruiters with active job posts.",
    },
    {
      id: 3,
      title: "Candidate Portal Design Consistency",
      portal: "Candidate Portal",
      icon: <AppstoreOutlined />,
      description:
        "A focused design pass across the candidate portal ensures consistent typography, button styles, and layout patterns throughout the application journey.",
      benefits: [
        "Unified Typography: Consistent font sizes and weights across all candidate-facing pages",
        "Button Standardization: Buttons follow a unified style guide for color, size, and spacing",
        "Layout Alignment: Section layouts are aligned consistently across assessment and dashboard pages",
        "Polished Experience: Reduced visual inconsistencies that affected the candidate's perceived quality",
      ],
      howToAccess: "Visible throughout the candidate portal",
    },
  ],
  bugFixes: [
    "Fixed Zoho Recruit pull mechanism sync failures and edge case handling",
    "Fixed report download failures and formatting issues in application reports",
    "Resolved UI bugs affecting layout and component rendering across admin portal",
    "Fixed font and button styling inconsistencies in the candidate portal",
    "Various bug fixes and stability enhancements across all portals",
  ],
  systemRequirements: {
    browsers: [
      "Google Chrome (latest)",
      "Microsoft Edge (latest)",
      "Mozilla Firefox (latest)",
      "Safari (latest)",
    ],
    aiAssessments: [
      "Webcam access required",
      "Microphone access required",
      "Screen sharing capability (for proctored assessments)",
      "System audio sharing (for certain assessment types)",
    ],
  },
};

// Version 2.1.0 Content
const v210Content: ReleaseNoteContent = {
  version: "2.1.0",
  releaseDate: "May 21, 2026",
  previousVersion: "2.0.9 (April 26, 2026)",
  overview:
    "QualifyMe v2.1.0 delivers AI-powered talent pool search, a rich candidate details screen within the talent pool, and significant enhancements to the Candidate Job Fit Report — including privacy controls to mask personal information and expanded content covering experience and education. This release also unifies candidate reminder notifications into a single, more reliable system and includes targeted bug fixes with no breaking changes.",
  features: [
    {
      id: 1,
      title: "AI-Based Talent Pool Search",
      portal: "Admin Portal",
      icon: <SearchOutlined />,
      description:
        "Recruiters can now search the talent pool using natural language queries powered by AI, making it significantly faster to find the right candidates from your existing pool.",
      benefits: [
        "Natural Language Queries: Search using plain English phrases like 'React developers with 5 years experience' instead of rigid keyword filters",
        "Semantic Matching: AI understands context and synonyms, surfacing relevant candidates even when exact keywords don't match",
        "Skill & Experience Filters: Combine AI search with structured filters for precise results",
        "Faster Discovery: Reduces the time spent manually scanning large candidate pools",
      ],
      howToAccess: "Talent Pool → Search bar → Enter a query to begin AI-powered search",
    },
    {
      id: 2,
      title: "Talent Pool Candidate Details Screen",
      portal: "Admin Portal",
      icon: <UserOutlined />,
      description:
        "A new dedicated details screen for talent pool candidates gives recruiters a comprehensive view of each candidate's profile, history, and job fit without leaving the talent pool.",
      benefits: [
        "Full Profile View: See candidate's complete profile including skills, experience, education, and previous applications",
        "Job Fit Summary: View AI-generated fit assessment scores and reasoning for relevant job posts",
        "Application History: Review all previous applications and their outcomes in one place",
        "Quick Actions: Shortlist, tag, or initiate contact directly from the details screen",
      ],
      howToAccess: "Talent Pool → Click on any candidate card to open the details screen",
    },
    {
      id: 3,
      title: "Privacy Controls for Candidate Job Fit Report",
      portal: "Admin Portal",
      icon: <LockOutlined />,
      description:
        "When downloading a Candidate Job Fit Report — either for a single candidate or in bulk — recruiters are now prompted to choose whether to mask the candidate's personal information before the download proceeds.",
      benefits: [
        "Mask on Download: Phone numbers and email addresses can be hidden in the downloaded PDF at the time of export",
        "Single & Bulk Reports: Privacy control applies to both individual application reports and bulk report downloads",
        "Anonymised Review: Share reports with external interviewers or panel members without exposing personal contact details",
        "No Permanent Change: Masking is applied only to the downloaded file — the platform data remains unchanged",
      ],
      howToAccess:
        "Application Details → Download Report → Choose 'Mask & Download' or 'Download As-Is' in the confirmation dialog",
      note: "The same privacy prompt appears when using the bulk report download from the Applications tab.",
    },
    {
      id: 4,
      title: "Enhanced Report Content — Experience & Education",
      portal: "Admin Portal",
      icon: <FileTextOutlined />,
      description:
        "The Candidate Job Fit Report now includes dedicated sections for work experience and educational background, giving reviewers a more complete picture of each candidate without needing to open the resume separately.",
      benefits: [
        "Work Experience Section: Lists the candidate's past roles, companies, durations, and key responsibilities",
        "Education Section: Displays degrees, institutions, graduation years, and relevant certifications",
        "Better Context for Reviewers: Interviewers and hiring managers get the full candidate story in a single document",
        "Consistent Formatting: Experience and education are presented in a clean, structured layout consistent with the rest of the report",
      ],
      howToAccess: "Application Details → Download Report → Open the downloaded PDF",
    },
    {
      id: 5,
      title: "Unified Reminder Notification System",
      portal: "Background",
      icon: <BellOutlined />,
      description:
        "Candidate reminder notifications have been consolidated into a single unified function, replacing the previous separate email and WhatsApp notification paths. This ensures all reminders are sent consistently regardless of the candidate's notification preference.",
      benefits: [
        "Single Entry Point: One unified reminder function handles all outbound notifications instead of separate email and WhatsApp calls",
        "Consistent Delivery: Reminders are now sent reliably through a centralised pipeline, reducing missed notifications",
        "Simplified Logic: Notification routing is handled internally, removing complexity from individual trigger points",
        "Easier Maintenance: Future changes to reminder behaviour only need to be made in one place",
      ],
      howToAccess:
        "Notifications are automatically sent — no configuration required from admins",
    },
    {
      id: 6,
      title: "Improved Assessment Reminder Notifications",
      portal: "Admin Portal",
      icon: <MailOutlined />,
      description:
        "The assessment reminder flow has been overhauled to use an event-based payload structure. Reminders now carry richer context, are dispatched through updated API endpoints, and correctly reflect the candidate's current assessment status at the time of sending.",
      benefits: [
        "Event-Based Payloads: Reminder notifications now include structured event data for accurate context",
        "Correct API Routing: Updated endpoints ensure reminders reach the right notification service every time",
        "Status-Aware Sending: System checks assessment status before sending reminders to avoid sending stale or irrelevant messages",
        "Reduced Duplicate Sends: Improved deduplication logic prevents candidates from receiving the same reminder multiple times",
      ],
      howToAccess:
        "Assessment reminders are sent automatically based on assessment deadlines and candidate progress",
    },
    {
      id: 7,
      title: "Notification Architecture Cleanup",
      portal: "Background",
      icon: <SyncOutlined />,
      description:
        "Internal notification code has been refactored for clarity and reliability. Redundant functions have been removed and the remaining notification utilities have been renamed to better reflect their purpose.",
      benefits: [
        "Removed Redundancy: Unused WhatsApp-specific message function has been removed from the codebase",
        "Clearer Naming: Notification functions have been renamed to accurately describe their current behaviour",
        "Leaner Service Layer: Reduced surface area in the notification service lowers the risk of regressions",
        "Better Traceability: Cleaner code makes it easier to trace notification flows during debugging",
      ],
      howToAccess: "Internal change — no user-facing action required",
    },
  ],
  bugFixes: [
    "Replaced separate email and WhatsApp reminder functions with a single unified reminder notification function",
    "Fixed typo in profile screening tag for unqualified status",
  ],
  systemRequirements: {
    browsers: [
      "Google Chrome (latest)",
      "Microsoft Edge (latest)",
      "Mozilla Firefox (latest)",
      "Safari (latest)",
    ],
    aiAssessments: [
      "Webcam access required",
      "Microphone access required",
      "Screen sharing capability (for proctored assessments)",
      "System audio sharing (for certain assessment types)",
    ],
  },
};

// Export all content
export const releaseNotesContentMap: Record<string, ReleaseNoteContent> = {
  "2.1.0": v210Content,
  "2.0.9": v209Content,
  "2.0.8": v208Content,
  "2.0.7": v207Content,
  "2.0.6": v206Content,
  "2.0.5": v205Content,
  "2.0.4": v204Content,
  "2.0.3": v203Content,
  "2.0.2": v202Content,
  "2.0.0": v200Content,
};

// List of all available versions with web release notes
export const WEB_RELEASE_NOTES_VERSIONS = Object.keys(releaseNotesContentMap);
